import { TablePagination } from "@mui/material";
import { DownloadOutline } from "assets/img/svg/downloadOutline";
import ModalBase from "components/shared/ModalBase";
import Pagination from "components/shared/Pagination";
import StyledButton from "components/shared/StyledButton";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import TextSkeleton from "components/shared/TextSkeleton";
import { formatDateToIso, formatLocaleDateString, formatNumber, isValidDDMMYYYYDate } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
    AdvanceProvisionList,
    AdvanceProvisionListParams,
    AdvanceProvisionSortKey,
    AdvanceProvisionSummary,
} from "store/advanceProvision";
import { exportAdvanceProvision, fetchAdvanceProvisionList } from "store/advanceProvision/advance-provision-use-cases";
import { useAppDispatch } from "store/store";

import AdvanceProvisionTable from "./AdvanceProvisionTable";

const SUMMARY_KEYS: (keyof AdvanceProvisionSummary)[] = [
    "termCount",
    "contractualAdvance",
    "paidAdvance",
    "unrecoupedBalance",
    "provisionAmount",
];
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const money = (cents: number) => formatNumber(cents / 100, { isCurrency: true, always2Digit: true });
// Calendar dates (yyyy-mm-dd) are UTC midnight: format them in UTC so they do not shift a day back
const formatCalendarDate = (value: string | null) =>
    value ? formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }) : "—";

type Props = {
    open: boolean;
    onClose: () => void;
};

/** Gestion › … › Provision des à-valoir: provision at a closing date, read-only. */
const AdvanceProvisionModal = ({ open, onClose }: Props) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [params, setParams] = useState<AdvanceProvisionListParams>({
        closingDate: formatDateToIso(new Date()),
        page: 1,
        pageSize: 25,
        sort: "beneficiaryName",
        order: "asc",
    });
    const [list, setList] = useState<AdvanceProvisionList | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const canExport = useAbility().can("export", "AdvanceProvision");

    useEffect(() => {
        if (!open) return;
        let isCurrent = true;
        setIsLoading(true);
        dispatch(fetchAdvanceProvisionList(params))
            .unwrap()
            .then((result) => isCurrent && setList(result))
            .catch(() => undefined)
            .finally(() => isCurrent && setIsLoading(false));
        return () => {
            isCurrent = false;
        };
    }, [dispatch, open, params]);

    const handleSort = (key: AdvanceProvisionSortKey) =>
        setParams((current) => ({
            ...current,
            page: 1,
            sort: key,
            order: current.sort === key && current.order === "asc" ? "desc" : "asc",
        }));

    const isTableLoading = isLoading || !list;
    // Paging and sorting keep the totals; a new closing date recomputes them
    const isSummaryLoading = !list || (isLoading && list.closingDate !== params.closingDate);

    return (
        <ModalBase
            open={open}
            onClose={onClose}
            modalTitle={t("AdvanceProvision.Management.Title")}
            modalSubtitle={t("AdvanceProvision.Management.Subtitle")}
            widthTransition={{ active: true, width: "min(1600px, 96vw)" }}
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="flex flex-col gap-1">
                        <span className="text-xs text-foreground">{t("AdvanceProvision.Management.ClosingDate")}</span>
                        <StyledDatePicker
                            inputFormat="dd/MM/yyyy"
                            maxYearDistance={5}
                            value={new Date(`${params.closingDate}T00:00:00`)}
                            onChange={(value, keyboardInputValue) => {
                                // The masked input fires on every keystroke: wait for a complete dd/mm/yyyy
                                const isComplete = !keyboardInputValue || isValidDDMMYYYYDate(keyboardInputValue);
                                if (isComplete && value && !Number.isNaN(new Date(value).getTime())) {
                                    setParams((current) => ({
                                        ...current,
                                        page: 1,
                                        closingDate: formatDateToIso(value),
                                    }));
                                }
                            }}
                        />
                    </div>
                    {canExport && (
                        <StyledButton
                            buttonType="secondary"
                            startIcon={<DownloadOutline width="16" height="16" />}
                            loading={isExporting}
                            disabled={isExporting}
                            onClick={async () => {
                                setIsExporting(true);
                                await dispatch(exportAdvanceProvision(params.closingDate));
                                setIsExporting(false);
                            }}
                        >
                            {t("AdvanceProvision.Management.Download")}
                        </StyledButton>
                    )}
                </div>

                <div className="grid grid-cols-2 rounded border border-border-color bg-white sm:grid-cols-5">
                    {SUMMARY_KEYS.map((key) => (
                        <div key={key} className="border-r border-border-color px-4 py-3 last:border-r-0">
                            <div className="text-xs text-foreground">
                                {t(`AdvanceProvision.Management.Summary.${key}`)}
                            </div>
                            <div className="mt-1 text-base font-medium">
                                {isSummaryLoading ? (
                                    <TextSkeleton width="6em" />
                                ) : key === "termCount" ? (
                                    list.summary.termCount
                                ) : (
                                    money(list.summary[key])
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <p className="text-xs text-foreground">
                    {t("AdvanceProvision.Management.TableNote", { date: formatCalendarDate(params.closingDate) })}
                </p>
                <AdvanceProvisionTable
                    rows={list?.rows ?? []}
                    isLoading={isTableLoading}
                    sort={params.sort}
                    order={params.order}
                    onSort={handleSort}
                />

                <TablePagination
                    component={() => (
                        <Pagination
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            rowsPerPage={params.pageSize}
                            currentPageSize={list?.rows.length ?? 0}
                            page={params.page - 1}
                            onPageChange={(_, page) => setParams((current) => ({ ...current, page: page + 1 }))}
                            onRowsPerPageChange={(event) =>
                                setParams((current) => ({ ...current, page: 1, pageSize: Number(event.target.value) }))
                            }
                            type="terms"
                            count={list?.total ?? 0}
                            sticky={false}
                        />
                    )}
                    count={-1}
                    rowsPerPage={params.pageSize}
                    page={params.page - 1}
                    onPageChange={(_, page) => setParams((current) => ({ ...current, page: page + 1 }))}
                />
            </div>
        </ModalBase>
    );
};

export default AdvanceProvisionModal;
